import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Dimensions,
  SafeAreaView,
  Alert,
  Modal,
  Vibration,
  Linking,
  Image,
} from 'react-native';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { Camera } from 'expo-camera';
import { BarCodeScanner } from 'expo-barcode-scanner';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { FontAwesome5, Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { scanQrCode, validateQrCode, transformScanResult, transformValidateResult } from '../services/qrScanApi';
import AppHeader from '../components/AppHeader';
import { getReferralAvailable, normalizeReferralPromotions, referralOptionsAfterScan } from '../services/referralApi';
import { formatIGBannerForDisplay, getIGBanner } from '../services/bannerApi';
import {
  navigateToClaimedCouponDetails,
  shouldNavigateToCouponDetails,
} from '../navigation/navigateToClaimedCoupon';
import { getPaygiftClaimModalUi } from '../utils/paygiftUserFacingMessage';
import { usePaygiftClaimBlockedModal } from '../components/PaygiftClaimBlockedModal';
import { HeroOutline, HeroSolid, HeroSolid20 } from '@nandorojo/heroicons';
import ScanClaimPrizeList from '../components/ScanClaimPrizeList';
import ScanSuccessNextActions from '../components/ScanSuccessNextActions';
import {
  formatPoinNumber,
  qrRewardSourceName,
  scanPointsFromLabel,
  scanStampsFromLabel,
  PointRewardHint,
  useConfettiAndClap
} from '../utils/scanClaimUi';
import { useCustomAlertModal } from '../components/CustomAlertModal';
import { useBannerWithPopUp } from '../components/BannerWithPopUp';

const { width, height } = Dimensions.get('window');

function PointIcon({size = 16, color = "#FFFFFF"}) {
  return (
    <Image 
      source={require('../../assets/icons/z-point-solid.svg')} 
      style={{ 
        width: size, 
        height: size, 
        tintColor: color
      }}
      resizeMode="contain"
    />
  )
}

export default function ScanScreen() {
  const navigation = useNavigation();
  const { showFromError: showPaygiftBlockedFromError, modal: paygiftBlockedModal } = usePaygiftClaimBlockedModal();
  const [hasPermission, setHasPermission] = useState(null);
  const [scanned, setScanned] = useState(false);
  const [flashOn, setFlashOn] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [userData, setUserData] = useState(null);
  const [redemptionModalVisible, setRedemptionModalVisible] = useState(false);
  const [pendingRedemption, setPendingRedemption] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [validationModalVisible, setValidationModalVisible] = useState(false);
  const [pendingQrData, setPendingQrData] = useState(null);
  const [isClaiming, setIsClaiming] = useState(false);
  const [polModal, setPolModal] = useState({
    visible: false,
    step: 'join',
    promotions: [],
    id_promosi: null,
    nama_promosi: null,
  });
  const {
    confettiWrapper,
    showConfetti
  } = useConfettiAndClap();
  const {
    customAlertModal,
    showCustomAlert,
    hideCustomAlert,
    handleCustomAlertConfirm
  } = useCustomAlertModal(() => {
    navigation.navigate('MainTabs', { screen: 'Home' });
  });
  const {
    bannerWrapper
  } = useBannerWithPopUp( {}, { borderRadius: 0, aspectRatio: 3.5 }, 'QRRewardResult' );

  /**
   * `scanned` stays true after first read until "Scan Again". Reset on focus when modals are closed so returning from Home (e.g. spin) can scan again without relogin.
   */
  useFocusEffect(
    useCallback(() => {
      if (
        !modalVisible &&
        !validationModalVisible &&
        !redemptionModalVisible &&
        !polModal.visible
      ) {
        setScanned(false);
      }
    }, [modalVisible, validationModalVisible, redemptionModalVisible, polModal.visible])
  );

  const [igBanner, setIGBanner] = useState({data: null, loading: true});

  useFocusEffect(
    useCallback(() => {
      const initScreen = async () => {
        await checkInitialPermissions();
        await loadUserData();
      }

      initScreen();
    }, [navigation])
  );

  useEffect(() => {
    if (validationModalVisible) {
      showConfetti();
    }
  }, [validationModalVisible]);

  const loadIGBanner = async () => {
    try {
      const response = await getIGBanner();
      
      if (response.success) {
        const formattedigBanner = formatIGBannerForDisplay(response.data);
        setIGBanner({data: formattedigBanner, loading: false});
      } else {
        console.log('Failed to load IG banner: ', response.error);
        setIGBanner({data: null, loading: false});
      }
    } catch (error) {
      console.log('Error loading IG banner: ', error);
      setIGBanner({data: null, loading: false});
    }
  };

  const checkInitialPermissions = async () => {
    const { status } = await Camera.getCameraPermissionsAsync();
    if (status === 'granted') {
      setHasPermission(true);
    } else {
      setHasPermission(false);
    }
  };

  const getCameraPermissions = async () => {
    const { status } = await Camera.requestCameraPermissionsAsync();
    setHasPermission(status === 'granted');
    
    // If permission is still denied, show alert to go to settings
    if (status === 'denied') {
      Alert.alert(
        'Camera Permission Required',
        'Camera access is required to scan QR codes. Please enable camera permission in your device settings.',
        [
          {
            text: 'Cancel',
            style: 'cancel',
          },
          {
            text: 'Open Settings',
            onPress: () => Linking.openSettings(),
          },
        ]
      );
    }
  };

  const loadUserData = async () => {
    try {
      const userToken = await AsyncStorage.getItem('userToken');
      const sessionId = await AsyncStorage.getItem('session_id');
      
      if (!userToken || !sessionId) {
        // Redirect to login if no authentication tokens
        navigation.navigate('Login');
        return;
      }
      
      const userDataString = await AsyncStorage.getItem('userData');
      if (userDataString) {
        const userDataStored = JSON.parse(userDataString);
        setUserData( userDataStored );

        // force incomplete user back to home (profile_completed_at only)
        if (!userDataStored?.user_profile_completed_at) {
          navigation.navigate('MainTabs', { screen: 'Home' });
        }
      }
    } catch (error) {
      console.log('Error loading user data:', error);
      navigation.navigate('Login');
    }
  };

  const isProfileIncomplete = (data = userData) => !data?.user_profile_completed_at;

  const isProfileIncompleteApiError = (error) => {
    const apiCode = error?.data?.error_code || error?.apiErrorCode;
    if (apiCode === 'PROFILE_INCOMPLETE') return true;
    const msg = String(error?.message || '').toLowerCase();
    return msg.includes('lengkapi profil') || msg.includes('profile_incomplete');
  };

  const blockIncompleteProfile = (message) => {
    showCustomAlert(
      'Profilmu Belum Lengkap',
      message || 'Lengkapi dahulu profilmu, untuk menikmati hadiah-hadiah dari Zoolyfe',
      'error'
    );
    setValidationModalVisible(false);
    setPendingQrData(null);
  };

  const handleBarCodeScanned = ({ type, data }) => {
    if (scanned) return;

    // Treat missing/unloaded userData as incomplete — do not allow scan race
    if (isProfileIncomplete()) {
      blockIncompleteProfile();
      return;
    }
    
    setScanned(true);
    Vibration.vibrate(100);
    
    // Process QR code data
    processQRCode(data);

    // Load newest IG banner
    loadIGBanner();
  };

  const processQRCode = async (qrData) => {
    try {
      if (isProfileIncomplete()) {
        blockIncompleteProfile();
        return;
      }

      const spinProgramCode = extractSpinProgramCode(qrData);
      if (spinProgramCode) {
        setValidationModalVisible(false);
        setModalVisible(false);
        setPendingQrData(null);
        navigation.navigate('MainTabs', {
          screen: 'Home',
          params: {
            openSpinFromQr: true,
            spinProgramCode: spinProgramCode,
            /* Unique route params so repeated Home navigations still merge and reopen spin popup */
            spinQrScanNonce: Date.now(),
          },
        });
        return;
      }

      // Remove any non-numeric characters from qrData
      const cleanedQrData = qrData.replace(/[^0-9]/g, '');
      console.log('Processing QR code');
      // console.log('Cleaned QR code:', cleanedQrData);
      
      // First call the validate endpoint to get QR info without claiming rewards
      const validateResponse = await validateQrCode(cleanedQrData);
      
      // Transform validation response for UI
      const validationResult = transformValidateResult(validateResponse);
      
      if (validationResult) {
        setPendingQrData(cleanedQrData);
        setValidationResult(validationResult);
        setValidationModalVisible(true);
        loadIGBanner();
      } else {
        throw new Error('Invalid validation response');
      }
      
    } catch (error) {
      console.error('Error processing QR code:', error);
      
      if (showPaygiftBlockedFromError(error)) {
        setValidationModalVisible(false);
        return;
      }

      // Handle different types of errors
      let errorResult;
      
      if (isProfileIncompleteApiError(error)) {
        blockIncompleteProfile(error.message);
        return;
      } else if (error.message.includes('sudah_scan') || error.message.includes('already scanned')) {
        errorResult = {
          type: 'already_scanned',
          title: 'Already Scanned',
          message: 'You have already scanned this QR code',
          icon: 'checkmark-circle',
          color: '#FF9800',
        };
      } else if (error.message.includes('expired')) {
        let expiredDate = null;

        if (error.data?.tanggal_berakhir && !isNaN(error.data?.tanggal_berakhir)) {
          expiredDate = new Date( Number( error.data.tanggal_berakhir ) * 1000 );
        }

        errorResult = {
          type: 'expired',
          title: 'QR Code Expired',
          // message: 'This QR code has expired and is no longer valid',
          message: 'Periode klaim voucher code telah berakhir',
          icon: 'time',
          color: '#F44336',
          errorData: {
            expiredDate: expiredDate
          }
        };
      } else if (error.message.includes('not found') || error.message.includes('404')) {
        errorResult = {
          type: 'invalid',
          title: 'Invalid QR Code',
          message: 'This QR code is not recognized by Zoolyfe Club',
          icon: 'close-circle',
          color: '#F44336',
        };
      } else if (error.message.includes('network') || error.message.includes('fetch')) {
        errorResult = {
          type: 'network',
          title: 'Connection Error',
          message: 'Please check your internet connection and try again',
          icon: 'wifi-off',
          color: '#F44336',
        };
      } else if (error.message.includes('Session ID is required')) {
        errorResult = {
          type: 'session_expired',
          title: 'Sesi telah berakhir.',
          message: 'Silakan login kembali untuk melanjutkan.',
          icon: 'lock-closed',
          color: '#F44336',
        };
      } else {
        errorResult = {
          type: 'error',
          title: 'Scan Failed',
          message: error.message || 'Failed to process QR code. Please try again.',
          icon: 'alert-circle',
          color: '#F44336',
        };
      }

      setScanResult(errorResult);
      setModalVisible(true);
    }
  };

  const resetScanner = () => {
    setScanned(false);
    setModalVisible(false);
    setScanResult(null);
    setRedemptionModalVisible(false);
    setPendingRedemption(null);
    setValidationModalVisible(false);
    setValidationResult(null);
    setPendingQrData(null);
  };

  const resetPolModal = () =>
    setPolModal({ visible: false, step: 'join', promotions: [], id_promosi: null, nama_promosi: null });

  const handleSuccessPopupButton = async () => {
    const result = scanResult;
    const wasSuccess = result && !['invalid', 'already_scanned', 'expired', 'network', 'error', 'session_expired'].includes(result?.type);
    const referralOpts = referralOptionsAfterScan(result);
    const openClaimedCouponOrList = () => {
      if (shouldNavigateToCouponDetails(result)) {
        navigateToClaimedCouponDetails(navigation, result.coupon, result.kawanActivation);
        return;
      }
      navigation.navigate('MainTabs', { screen: 'Coupons' });
    };
    resetScanner();
    if (!wasSuccess) return;
    if (!referralOpts) {
      resetPolModal();
      if (shouldNavigateToCouponDetails(scanResult)) {
        openClaimedCouponOrList();
      }
      return;
    }
    if (polModal.step === 'pick' && polModal.promotions?.length) {
      setPolModal((prev) => ({ ...prev, visible: true }));
      return;
    }
    if (polModal.id_promosi) {
      setPolModal((prev) => ({ ...prev, visible: true }));
      return;
    }
    try {
      const r = await getReferralAvailable(referralOpts);
      const promos = normalizeReferralPromotions(r);
      if (promos.length === 1) {
        setPolModal({
          visible: true,
          step: 'join',
          promotions: [],
          id_promosi: promos[0].id_promosi,
          nama_promosi: promos[0].nama_promosi || '',
        });
      } else if (promos.length > 1) {
        setPolModal({
          visible: true,
          step: 'pick',
          promotions: promos,
          id_promosi: null,
          nama_promosi: null,
        });
      } else if (shouldNavigateToCouponDetails(scanResult)) {
        openClaimedCouponOrList();
      }
    } catch (_) {
      if (shouldNavigateToCouponDetails(scanResult)) {
        openClaimedCouponOrList();
      }
    }
  };

  const handleLihatPoin = () => {
    resetPolModal();
    resetScanner();
    navigation.navigate('MainTabs', {
      screen: 'TukarPoin',
      params: { tab: 'promo' },
    });
  };

  const handleAcceptReward = async () => {
    try {
      if (isProfileIncomplete()) {
        blockIncompleteProfile();
        return;
      }

      if (!validationResult || !pendingQrData) {
        throw new Error('No validation data available');
      }

      setIsClaiming(true);

      // Claim via scan endpoint (use kode_qr, not qr_code.id — same as HomeScreen / link flow)
      const apiResponse = await scanQrCode(pendingQrData);
      
      // Transform API response for UI
      const result = transformScanResult(apiResponse);
      
      if (result) {
        setPendingQrData(null);
        // Update local user data with new balance
        if (result.userBalance && userData) {
          const updatedUserData = {
            ...userData,
            points: result.userBalance.totalPoints,
            stamps: result.userBalance.totalStamps,
          };
          await AsyncStorage.setItem('userData', JSON.stringify(updatedUserData));
          setUserData(updatedUserData);
        }
        
        setScanResult(result);
        setValidationModalVisible(false);
        setModalVisible(true);
        const isSuccessType = result && !['invalid', 'already_scanned', 'expired', 'network', 'error', 'session_expired', 'profile_incomplete'].includes(result.type);
        const refOptsAfterScan = referralOptionsAfterScan(result);
        if (isSuccessType && !refOptsAfterScan) {
          setPolModal({ visible: false, step: 'join', promotions: [], id_promosi: null, nama_promosi: null });
        }
        if (isSuccessType && refOptsAfterScan) {
          getReferralAvailable(refOptsAfterScan).then((r) => {
            const promos = normalizeReferralPromotions(r);
            if (promos.length === 1) {
              setPolModal({
                visible: false,
                step: 'join',
                promotions: [],
                id_promosi: promos[0].id_promosi,
                nama_promosi: promos[0].nama_promosi || '',
              });
            } else if (promos.length > 1) {
              setPolModal({
                visible: false,
                step: 'pick',
                promotions: promos,
                id_promosi: null,
                nama_promosi: null,
              });
            }
          }).catch(() => {});
        }
      } else {
        throw new Error('Invalid API response');
      }
      
    } catch (error) {
      console.error('Error accepting reward:', error);

      if (showPaygiftBlockedFromError(error)) {
        setValidationModalVisible(false);
        return;
      }
      
      // Handle error and show appropriate message
      let errorResult;
      
      if (isProfileIncompleteApiError(error)) {
        blockIncompleteProfile(error.message);
        return;
      } else if (error.message.includes('sudah_scan') || error.message.includes('already scanned')) {
        errorResult = {
          type: 'already_scanned',
          title: 'Already Scanned',
          message: 'You have already scanned this QR code',
          icon: 'checkmark-circle',
          color: '#FF9800',
        };
      } else if (error.message.includes('expired')) {
        errorResult = {
          type: 'expired',
          title: 'QR Code Expired',
          message: 'This QR code has expired and is no longer valid',
          icon: 'time',
          color: '#F44336',
        };
      } else if (error.message.includes('not found') || error.message.includes('404')) {
        errorResult = {
          type: 'invalid',
          title: 'Invalid QR Code',
          message: 'This QR code is not recognized by Zoolyfe Club',
          icon: 'close-circle',
          color: '#F44336',
        };
      } else if (error.message.includes('network') || error.message.includes('fetch')) {
        errorResult = {
          type: 'network',
          title: 'Connection Error',
          message: 'Please check your internet connection and try again',
          icon: 'wifi-off',
          color: '#F44336',
        };
      } else if (error.message.includes('Session ID is required')) {
        errorResult = {
          type: 'session_expired',
          title: 'Sesi telah berakhir.',
          message: 'Silakan login kembali untuk melanjutkan.',
          icon: 'lock-closed',
          color: '#F44336',
        };
      } else {
        errorResult = {
          type: 'error',
          title: 'Scan Failed',
          message: error.message || 'Failed to process QR code. Please try again.',
          icon: 'alert-circle',
          color: '#F44336',
        };
      }

      setScanResult(errorResult);
      setValidationModalVisible(false);
      setModalVisible(true);
    } finally {
      setIsClaiming(false);
    }
  };
  

  const renderScanOverlay = () => (
    <View style={styles.overlay}>
      <View style={styles.topOverlay} />
      <View style={styles.middleRow}>
        <View style={styles.sideOverlay} />
        <View style={styles.scanArea}>
          <View style={styles.scanFrame}>
            <View style={[styles.corner, styles.topLeft]} />
            <View style={[styles.corner, styles.topRight]} />
            <View style={[styles.corner, styles.bottomLeft]} />
            <View style={[styles.corner, styles.bottomRight]} />
          </View>
        </View>
        <View style={styles.sideOverlay} />
      </View>
      <View style={styles.bottomOverlay}>
        <Text style={styles.instructionText}>
          Position the QR code within the frame to scan
        </Text>
      </View>
    </View>
  );

  const isSuccessResult = scanResult && !['already_used', 'already_scanned', 'expired', 'invalid', 'network', 'error'].includes(scanResult?.type);

  const renderResultModal = () => (
    <Modal
      animationType="fade"
      transparent={true}
      visible={modalVisible}
      onRequestClose={resetScanner}
    >
      <View style={styles.modalOverlay}>
        <View style={[styles.modalContainer, styles.validationModalContent]}>
          {scanResult && (
            <ScrollView
              style={styles.validationScrollView}
              showsVerticalScrollIndicator={true}
              keyboardShouldPersistTaps="handled"
            >
              <LinearGradient
                colors={isSuccessResult 
                  ? ['#4CAF50', '#4CAF5080'] 
                  : [scanResult.color, scanResult.color + '80']}
                style={[styles.resultHeader, { padding: 0 }]}
              >
                <View style={styles.resultHeaderContainer}>
                  {!isSuccessResult && (
                    <View style={[styles.resultIcon, { marginBottom: 0 }]}>
                      {isSuccessResult ? (
                        <HeroSolid.CheckBadge height="30" width="30" color="#FFFFFF" />
                      ) : (
                        <HeroSolid.XCircle height="30" width="30" color="#FFFFFF" />
                      )}
                    </View>
                  )}
                  <View style={styles.resultTitleWrapper}>
                    <Text style={[styles.resultTitle, { marginBottom: 0 }, !isSuccessResult && styles.resultTitleLeft]}>
                      {scanResult.type === 'session_expired'
                        ? scanResult.title
                        : isSuccessResult ? (scanResult.title || 'Redeem Berhasil!') : 'Redeem Gagal'}
                    </Text>
                    {scanResult.type !== 'coupon' && (
                      <Text style={[styles.resultMessage, !isSuccessResult && styles.resultMessageLeft]}>
                        {scanResult.message || (isSuccessResult ? 'QR Code berhasil di-redeem!' : 'Terjadi kesalahan saat redeem')}
                      </Text>
                    )}
                  </View>
                </View>
                {isSuccessResult ? (
                  <View style={styles.bannerWrapper}>
                    {bannerWrapper}
                  </View>
                ) : null}
              </LinearGradient>

              {scanResult?.errorData && (
                <View style={[styles.resultDetails, styles.expiredContainer]}>
                  {scanResult?.type === 'expired' && (
                    <>
                      <Text style={[styles.sectionTitle, styles.expiredText]}>Periode klaim voucher telah berakhir{scanResult?.errorData?.expiredDate ? ` pada` : ``}</Text>
                      
                      {scanResult?.errorData?.expiredDate && (
                        <Text style={[styles.sectionTitle, styles.expiredText, styles.expiredDateText]}>{scanResult?.errorData?.expiredDate.toLocaleDateString('id-ID', {day: 'numeric', month: 'long', year: 'numeric'})}</Text>
                      )}

                      <Text style={[styles.sectionTitle, styles.expiredText]}>Nantikan program berikutnya.</Text>
                    </>
                  )}
                </View>
              )}
              
              <View style={styles.resultDetails}>
                
                {(scanResult.type === 'all' || scanResult.type.includes('points')) && scanResult.rewards?.points > 0 && (
                  <View style={styles.rewardInfo}>
                    <Text style={styles.rewardLabel}>Points Earned</Text>
                    <View style={styles.rewardValueRow}>
                      <Text style={styles.rewardValue}>
                        +{formatPoinNumber(scanResult.rewards?.points)} {scanResult.rewards?.points === 1 ? 'Point' : 'Points'}
                      </Text>
                      {qrRewardSourceName(scanResult.qrCode) ? (
                        <Text style={styles.rewardBrandText}> dari {qrRewardSourceName(scanResult.qrCode)}</Text>
                      ) : null}
                    </View>
                  </View>
                )}
                
                {(scanResult.type === 'all' || scanResult.type.includes('stamp')) && scanResult.rewards?.stamps > 0 && (
                  <View style={styles.rewardInfo}>
                    <Text style={styles.rewardLabel}>E-Stamps Collected</Text>
                    <View style={styles.rewardValueRow}>
                      <Text style={styles.rewardValue}>
                        +{scanResult.rewards?.stamps} {scanResult.rewards?.stamps === 1 ? 'e-stamp' : 'e-stamps'}
                      </Text>
                      {qrRewardSourceName(scanResult.qrCode) ? (
                        <Text style={styles.rewardBrandText}> dari {qrRewardSourceName(scanResult.qrCode)}</Text>
                      ) : null}
                    </View>
                  </View>
                )}
                
                {(scanResult.type === 'all' || scanResult.type.includes('coupon')) && scanResult.coupon && (
                  <View style={styles.rewardAllContainer}>
                    <View style={styles.rewardAllSection}>
                      <View style={styles.rewardAllHeader}>
                        <Ionicons name="gift" size={20} color="#FF6B35" style={styles.rewardAllIcon} />
                        <Text style={styles.rewardAllSectionTitle}>Reward Kamu</Text>
                      </View>
                      <View style={styles.rewardAllDivider} />
                      {!!scanResult.paygiftClaimPending && (
                        <Text style={styles.rewardAllValidityNote}>
                          {(scanResult.paygiftClaimModalUi
                            || getPaygiftClaimModalUi(scanResult.coupon)
                          ).queueNotice}
                        </Text>
                      )}
                      <Text style={styles.rewardAllVoucherName}>{scanResult.coupon.nama_kupon}</Text>
                      <Text style={styles.rewardAllValidityNote}>Voucher berlaku 30 hari setelah diklaim.</Text>
                    </View>
                    <View style={styles.rewardAllDivider} />
                    <View style={styles.rewardAllSection}>
                      <Text style={styles.rewardAllStatusTitle}>Status Voucher</Text>
                      <Text style={styles.rewardAllStatusValue}>
                        {(scanResult.coupon.tipe_kupon === 'hadiah_produk' || scanResult.coupon.type === 'hadiah_produk')
                          ? 'Lihat kode validasi di Kupon'
                          : scanResult.paygiftClaimPending
                            ? (scanResult.paygiftClaimModalUi
                                || getPaygiftClaimModalUi(scanResult.coupon)
                              ).statusLabel
                            : 'Siap digunakan'}
                      </Text>
                      <Text style={styles.rewardAllUsageNote}>
                        {(scanResult.coupon.tipe_kupon === 'hadiah_produk' || scanResult.coupon.type === 'hadiah_produk')
                          ? 'Kode validasi hadiah produk ada di menu Kupon - Shopping Coupon.'
                          : scanResult.paygiftClaimPending
                            ? (scanResult.paygiftClaimModalUi
                                || getPaygiftClaimModalUi(scanResult.coupon)
                              ).usageNote
                            : 'Voucher dapat digunakan melalui menu Kupon - Shopping Coupon'}
                      </Text>
                    </View>
                  </View>
                )}
                
                {scanResult.type !== 'coupon' && scanResult.userBalance && (
                  <View style={styles.userStats}>
                    <Text style={styles.statsTitle}>Your Current Balance</Text>
                    <View style={styles.statsRow}>
                      {scanResult.userBalance?.totalPoints !== 0 && (
                        <View style={styles.statItem}>
                          <PointIcon size={20} color={"#FF6B35"} />
                          <Text style={styles.statText}>{formatPoinNumber(scanResult.userBalance.totalPoints)} points</Text>
                        </View>
                      )}
                      {scanResult.userBalance?.totalStamps !== 0 && (
                        <View style={styles.statItem}>
                          <HeroSolid.Bookmark height="20" width="20" color="#4CAF50" />
                          <Text style={styles.statText}>{scanResult.userBalance.totalStamps} stamps</Text>
                        </View>
                      )}
                    </View>
                  </View>
                )}
                

              </View>
              
              <ScanSuccessNextActions
                scanResult={scanResult}
                primaryColor={scanResult?.color || '#FF6B35'}
                onPrimary={handleSuccessPopupButton}
                onLihatPoin={handleLihatPoin}
              />
            </ScrollView>
          )}
        </View>
      </View>
    </Modal>
  );

  const renderValidationModal = () => (
    <Modal
      animationType="slide"
      transparent={true}
      visible={validationModalVisible}
      onRequestClose={resetScanner}
    >
      <View style={styles.modalOverlay}>
        <View style={[styles.modalContent, styles.validationModalContent]}>
          {validationResult && (
            <ScrollView
              style={[styles.validationScrollView, {position: 'relative'}]}
              contentContainerStyle={styles.validationScrollContent}
              showsVerticalScrollIndicator={true}
              keyboardShouldPersistTaps="handled"
            >
              {confettiWrapper}
              <View
                style={[styles.resultHeader, styles.validationResultHeader]}
              >
                {validationResult.qrCode?.brand?.logo && (
                  <View style={styles.brandLogoContainer}>
                    <Image
                      source={{ uri: validationResult.qrCode.brand.logo }}
                      style={styles.brandLogo}
                      resizeMode="contain"
                    />
                  </View>
                )}
                <Text style={[styles.resultTitle, styles.validationResultTitle]}>{validationResult.title} <Text style={{fontSize: 16, marginLeft: 2}}>🎉</Text></Text>
                <Text style={[styles.resultMessage, styles.validationResultTitle]}>{validationResult.message}</Text>
              </View>
                  
              <View style={{
                ...styles.horizontalDashedLine,
                marginVertical: 0,
                marginHorizontal: 16,
              }}></View>
              
              <View style={styles.resultDetails}>
                
                {/* Rewards Information */}
                <View style={styles.rewardsSection}>
                  <Text style={styles.sectionTitle}>Kamu mendapatkan</Text>
                  
                  {(validationResult.rewards?.points || 0) > 0 && (
                    <View style={styles.rewardItem}>
                      <PointIcon size={20} color={"#F59E0B"} />
                      <View style={styles.pointWrapper}>
                        <Text style={styles.rewardText}>
                          {scanPointsFromLabel(validationResult.rewards.points, validationResult.qrCode)}
                        </Text>
                        <Text style={styles.pointConvertion}>{`(≈ Rp. ${formatPoinNumber(Number(validationResult.rewards.points || 0) * 100)})`}</Text>
                      </View>
                    </View>
                  )}
                  
                  {(validationResult.rewards?.stamps || 0) > 0 && (
                    <View style={styles.rewardItem}>
                      <HeroSolid.Bookmark height="20" width="20" color="#4CAF50" />
                      <Text style={styles.rewardText}>
                        {scanStampsFromLabel(validationResult.rewards.stamps, validationResult.qrCode)}
                      </Text>
                    </View>
                  )}
                  
                  {validationResult.rewards?.hasCoupon && validationResult.couponDetails && (
                    <View style={styles.rewardItem}>
                      <HeroSolid.Ticket height="20" width="20" color="#9C27B0" />
                      <Text style={styles.rewardText}>
                        {validationResult.couponDetails.nama_kupon}
                      </Text>
                    </View>
                  )}
                </View>

              </View>

              {(validationResult.rewards?.points || 0) > 0 && (<PointRewardHint />)}

              <View style={[styles.resultDetails, {paddingTop: 0}]}>

                <ScanClaimPrizeList
                  titleStyle={styles.sectionTitle}
                  itemStyle={styles.rewardItem}
                  textStyle={styles.rewardText}
                />
                
              </View>
              
              {/* Action Buttons */}
              <View style={styles.validationActions}>
                <TouchableOpacity
                  style={[styles.validationButton, styles.cancelButton]}
                  onPress={resetScanner}
                >
                  <Text style={styles.cancelButtonText}>Cancel</Text>
                </TouchableOpacity>
                
                <TouchableOpacity
                  style={[styles.validationButton, styles.acceptButton, isClaiming && styles.disabledButton]}
                  onPress={handleAcceptReward}
                  disabled={isClaiming}
                >
                  <Text style={styles.acceptButtonText}>{isClaiming ? `Memproses...` : `Klaim Reward`}</Text>
                </TouchableOpacity>
              </View> 

              <View style={styles.resultDetails}>
                {/* QR Code Information */}
                <View style={styles.qrInfoSection}>
                  <Text style={[styles.sectionTitle, styles.validationResultMiniSectionTitle]}>Voucher Information</Text>
                  <Text style={[styles.qrName, styles.validationResultMiniSectionName]}>
                    {(validationResult.qrCode?.nama || '').replace(/\s*-\s*\d+$/, '')}
                  </Text>
                  {!!validationResult.qrCode.deskripsi && (
                    <Text style={[styles.qrDescription, styles.validationResultMiniSectionDescription]}>{validationResult.qrCode.deskripsi}</Text>
                  )}
                </View>

                {validationResult.rewards?.hasCoupon && validationResult.couponDetails && (
                  <>
                    <View style={{
                      ...styles.horizontalLine,
                      marginVertical: 8
                    }}></View>
                    <View style={styles.qrInfoSection}>
                      <Text style={[styles.sectionTitle, styles.validationResultMiniSectionTitle]}>Coupon Details</Text>

                      {validationResult.couponDetails.tipe_kupon === 'diskon_persen' && (
                        <Text style={[styles.qrDescription, styles.validationResultMiniSectionName]}>{validationResult.couponDetails.nilai_diskon}% Discount</Text>
                      )}
                      {validationResult.couponDetails.tipe_kupon === 'diskon_nominal' && (
                        <Text style={[styles.qrDescription, styles.validationResultMiniSectionName]}>Rp {validationResult.couponDetails.nilai_diskon.toLocaleString()} Off</Text>
                      )}

                      <Text style={[styles.qrDescription, styles.validationResultMiniSectionName]}>{validationResult.couponDetails.deskripsi}</Text>
                      <Text style={[styles.qrDescription, styles.validationResultMiniSectionDescription]}>
                        {`Berlaku hingga: ${new Date(validationResult.couponDetails.tanggal_berakhir).toLocaleDateString('id-ID', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric'
                        })}`}
                      </Text>
                    </View>
                  </>
                )}
              </View>

              {igBanner.data ? (
                <View style={styles.bannerContainer}>
                  <View style={styles.banner}>
                    <Image 
                      style={styles.bannerImage}
                      source={{uri: igBanner.data.background}}
                      resizeMode='cover'
                    ></Image>
                    <View style={styles.bannerOverlay}></View>
                    <View style={styles.bannerContent}>
                      <Image 
                        style={styles.bannerLogo}
                        source={{uri: igBanner.data.logo}}
                        resizeMode='contain'
                      ></Image>
                      <Text style={styles.bannerTitle}>{igBanner.data.title}</Text>
                      <View style={styles.bannerButtonContainer}>
                        {(igBanner.data.urls || []).map((url, index) => {
                          return (
                            <TouchableOpacity 
                              key={index}
                              style={styles.bannerButton}
                              onPress={() => window.open(url.url, '_blank')}
                            >
                              <FontAwesome5
                                style={styles.bannerButtonIcon}
                                name={url.type == "X" ? "twitter" : url.type.toLowerCase()} 
                              />
                              <Text style={styles.bannerButtonText}>{url.title}</Text>
                            </TouchableOpacity>
                          )
                        })}
                      </View>
                    </View>
                  </View>
                </View>
              ) : null}
            </ScrollView>
          )}
        </View>
      </View>
    </Modal>
  );

  if (hasPermission === null) {
    return (
      <View style={styles.permissionContainer}>
        <Text style={styles.permissionText}>Requesting camera permission...</Text>
      </View>
    );
  }

  if (hasPermission === false) {
    return (
      <View style={styles.permissionContainer}>
        <Ionicons name="camera-off" size={64} color="#CCC" />
        <Text style={styles.permissionTitle}>Camera Access Required</Text>
        <Text style={styles.permissionText}>
          Please enable camera access in your device settings to scan QR codes
        </Text>
        <TouchableOpacity style={styles.settingsButton} onPress={getCameraPermissions}>
          <Text style={styles.settingsButtonText}>Grant Permission</Text>
        </TouchableOpacity>
      </View>
    );
  }

  // console.log('Check scanResult :', scanResult);

  return (
    <SafeAreaView style={styles.container}>
      <AppHeader title="Scan QR Code" showBack={false} />
      
      <View style={styles.cameraContainer}>
        <Camera
          style={styles.camera}
          type={Camera.Constants.Type.back}
          flashMode={flashOn ? Camera.Constants.FlashMode.torch : Camera.Constants.FlashMode.off}
          onBarCodeScanned={scanned ? undefined : handleBarCodeScanned}
          barCodeScannerSettings={{
            barCodeTypes: [BarCodeScanner.Constants.BarCodeType.qr],
          }}
        >
          {renderScanOverlay()}
        </Camera>
      </View>
      
      <View style={styles.controls}>
        <TouchableOpacity
          style={styles.controlButton}
          onPress={() => setFlashOn(!flashOn)}
        >
          <Ionicons 
            name={flashOn ? "flash" : "flash-off"} 
            size={24} 
            color={flashOn ? "#FF6B35" : "#666"} 
          />
          <Text style={[styles.controlText, flashOn && styles.activeControlText]}>Flash</Text>
        </TouchableOpacity>
        
        <TouchableOpacity
          style={[styles.controlButton, styles.scanButton]}
          onPress={resetScanner}
          disabled={!scanned}
        >
          <Ionicons name="scan" size={24} color="#FFFFFF" />
          <Text style={styles.scanButtonText}>Scan Again</Text>
        </TouchableOpacity>
        
        <TouchableOpacity style={styles.controlButton}>
          <Ionicons name="help-circle-outline" size={24} color="#666" />
          <Text style={styles.controlText}>Help</Text>
        </TouchableOpacity>
      </View>
      
      {renderResultModal()}
      {renderValidationModal()}
      <Modal
        animationType="fade"
        transparent={true}
        visible={polModal.visible}
        onRequestClose={resetPolModal}
      >
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, { maxWidth: 340 }]}>
            <Text style={styles.resultTitle}>
              {polModal.step === 'pick' ? 'Pilih program POL' : 'Gabung sebagai POL?'}
            </Text>
            {polModal.step === 'pick' && polModal.promotions?.length ? (
              <>
                <Text style={{ fontSize: 14, color: '#64748B', textAlign: 'center', marginBottom: 12 }}>
                  Buka menu Kode Referral untuk membuat kode di program yang Anda pilih.
                </Text>
                <ScrollView style={{ maxHeight: 260 }} keyboardShouldPersistTaps="handled">
                  {polModal.promotions.map((p) => (
                    <TouchableOpacity
                      key={p.id_promosi}
                      style={{
                        padding: 14,
                        borderWidth: 1,
                        borderColor: '#E2E8F0',
                        borderRadius: 10,
                        marginBottom: 10,
                        backgroundColor: '#F8FAFC',
                      }}
                      onPress={() => {
                        resetPolModal();
                        navigation.navigate('Referral');
                      }}
                    >
                      <Text style={{ fontWeight: '700', fontSize: 15, color: '#0F172A' }}>{p.nama_promosi}</Text>
                      {p.scope_line ? (
                        <Text style={{ fontSize: 12, color: '#64748B', marginTop: 4 }} numberOfLines={3}>
                          {p.scope_line}
                        </Text>
                      ) : null}
                    </TouchableOpacity>
                  ))}
                </ScrollView>
                <TouchableOpacity
                  style={[styles.continueButton, { backgroundColor: '#94A3B8', marginTop: 8 }]}
                  onPress={resetPolModal}
                >
                  <Text style={styles.continueButtonText}>Tutup</Text>
                </TouchableOpacity>
              </>
            ) : (
              <>
                {polModal.nama_promosi ? (
                  <Text style={{ fontSize: 15, fontWeight: '600', color: '#0F172A', textAlign: 'center', marginBottom: 8 }}>
                    {polModal.nama_promosi}
                  </Text>
                ) : null}
                <Text style={{ fontSize: 14, color: '#64748B', textAlign: 'center', marginBottom: 20 }}>
                  Lanjut ke halaman Kode Referral untuk membuat kode unik dan klaim hadiah.
                </Text>
                <View style={{ flexDirection: 'row', gap: 12, justifyContent: 'center' }}>
                  <TouchableOpacity
                    style={[styles.continueButton, { backgroundColor: '#94A3B8', flex: 1 }]}
                    onPress={() => {
                      resetPolModal();
                      navigation.navigate('MainTabs', { screen: 'Coupons' });
                    }}
                  >
                    <Text style={styles.continueButtonText}>Nanti</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.continueButton, { backgroundColor: '#64748B', flex: 1 }]}
                    onPress={() => {
                      resetPolModal();
                      navigation.navigate('Referral');
                    }}
                  >
                    <Text style={styles.continueButtonText}>Lanjut</Text>
                  </TouchableOpacity>
                </View>
              </>
            )}
          </View>
        </View>
      </Modal>

      {customAlertModal}
      {paygiftBlockedModal}

    </SafeAreaView>
  );
}

function extractSpinProgramCode(raw) {
  const value = String(raw || '').trim();
  if (!value) return null;

  const directCodeMatch = value.match(/^SW[A-Z0-9]+$/i);
  if (directCodeMatch) {
    return directCodeMatch[0].toUpperCase();
  }

  const fromPathMatch = value.match(/\/spin-wheel\/([^/?#]+)/i);
  if (fromPathMatch && fromPathMatch[1]) {
    return decodeURIComponent(fromPathMatch[1]).trim().toUpperCase();
  }

  return null;
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#000000',
  },
  permissionContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F8F9FA',
    paddingHorizontal: 40,
  },
  permissionTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333',
    marginTop: 20,
    marginBottom: 10,
    textAlign: 'center',
  },
  permissionText: {
    fontSize: 16,
    color: '#666',
    textAlign: 'center',
    lineHeight: 22,
    marginBottom: 30,
  },
  settingsButton: {
    backgroundColor: '#FF6B35',
    paddingHorizontal: 30,
    paddingVertical: 12,
    borderRadius: 25,
  },
  settingsButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: 'bold',
  },
  cameraContainer: {
    flex: 1,
    position: 'relative',
  },
  camera: {
    flex: 1,
  },
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  topOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
  },
  middleRow: {
    flexDirection: 'row',
    height: 250,
  },
  sideOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
  },
  scanArea: {
    width: 250,
    height: 250,
    justifyContent: 'center',
    alignItems: 'center',
  },
  scanFrame: {
    width: 200,
    height: 200,
    position: 'relative',
  },
  corner: {
    position: 'absolute',
    width: 20,
    height: 20,
    borderColor: '#999',
    borderWidth: 3,
  },
  topLeft: {
    top: 0,
    left: 0,
    borderRightWidth: 0,
    borderBottomWidth: 0,
  },
  topRight: {
    top: 0,
    right: 0,
    borderLeftWidth: 0,
    borderBottomWidth: 0,
  },
  bottomLeft: {
    bottom: 0,
    left: 0,
    borderRightWidth: 0,
    borderTopWidth: 0,
  },
  bottomRight: {
    bottom: 0,
    right: 0,
    borderLeftWidth: 0,
    borderTopWidth: 0,
  },
  bottomOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 40,
  },
  instructionText: {
    color: '#FFFFFF',
    fontSize: 16,
    textAlign: 'center',
    lineHeight: 22,
  },
  controls: {
    display: 'none',
    flexDirection: 'row',
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 20,
    paddingVertical: 20,
    justifyContent: 'space-around',
    alignItems: 'center',
  },
  controlButton: {
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 15,
  },
  scanButton: {
    backgroundColor: '#222',
    borderRadius: 25,
    paddingHorizontal: 25,
    paddingVertical: 12,
  },
  controlText: {
    fontSize: 12,
    color: '#666',
    marginTop: 5,
  },
  activeControlText: {
    color: '#FF6B35',
    fontWeight: 'bold',
  },
  scanButtonText: {
    fontSize: 12,
    color: '#FFFFFF',
    marginTop: 5,
    fontWeight: 'bold',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 20,
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    width: '100%',
    maxWidth: 400,
    overflow: 'hidden',
  },
  validationModalContent: {
    maxHeight: height * 0.88,
    maxWidth: 360,
    flexDirection: 'column',
  },
  validationScrollView: {
    flex: 1,
    minHeight: 0,
  },
  validationScrollContent: {
    // paddingBottom: 24,
  },
  modalContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 0,
    overflow: 'hidden',
    width: '100%',
    maxWidth: 400,
    shadowColor: '#000000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 8,
    elevation: 8,
  },
  resultHeader: {
    padding: 16,
    alignItems: 'center',
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    overflow: 'hidden',
  },
  brandLogoContainer: {
    width: 60,
    height: 60,
    borderRadius: 'calc(infinity * 1px)',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 8,
    // padding: 10,
  },
  brandLogo: {
    width: 60,
    height: 60,
  },
  resultIcon: {
    width: 48,
    height: 48,
    borderRadius: 'calc(infinity * 1px)',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 15,
  },
  resultTitle: {
    color: '#FFFFFF',
    fontSize: 18,
    lineHeight: 24,
    fontWeight: 600,
    marginBottom: 8,
    textAlign: 'center',
    fontFamily: 'Poppins-SemiBold'
  },
  resultMessage: {
    color: 'rgba(255, 255, 255, 0.9)',
    fontSize: 14,
    textAlign: 'center',
    lineHeight: 20,
    fontFamily: 'Poppins',
    fontWeight: 400,
  },
  brandName: {
    color: 'rgba(255, 255, 255, 0.8)',
    fontSize: 14,
    textAlign: 'center',
    marginTop: 8,
    fontWeight: '600',
  },
  resultDetails: {
    padding: 16,
  },
  rewardInfo: {
    alignItems: 'center',
    marginBottom: 20,
  },
  rewardLabel: {
    fontSize: 14,
    lineHeight: 20,
    fontWeight: 500,
    color: '#666',
    marginBottom: 5,
    fontFamily: 'Poppins-Medium'
  },
  rewardValueRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'center',
  },
  rewardValue: {
    fontSize: 14,
    lineHeight: 20,
    fontWeight: 600,
    color: '#FF6B35',
    fontFamily: 'Poppins-SemiBold'
  },
  rewardBrandText: {
    fontSize: 14,
    lineHeight: 20,
    fontWeight: 400,
    color: '#666',
    marginLeft: 4,
    fontFamily: 'Poppins'
  },
  couponInfo: {
    alignItems: 'center',
    marginBottom: 20,
  },
  couponLabel: {
    fontSize: 14,
    color: '#666',
    marginBottom: 8,
  },
  couponName: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    textAlign: 'center',
    marginBottom: 8,
  },
  couponCode: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FF6B35',
    textAlign: 'center',
    marginBottom: 8,
  },
  couponDiscount: {
    fontSize: 20,
    fontWeight: 700,
    color: '#4CAF50',
    textAlign: 'center',
    marginBottom: 12,
    fontFamily: 'Poppins'
  },
  couponNote: {
    fontSize: 14,
    color: '#666',
    textAlign: 'center',
    fontStyle: 'italic',
    marginBottom: 15,
  },
  rewardAllContainer: {
    width: '100%',
    marginBottom: 20,
  },
  rewardAllSection: {
    paddingVertical: 12,
  },
  rewardAllHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  rewardAllIcon: {
    marginRight: 8,
  },
  rewardAllSectionTitle: {
    fontSize: 14,
    fontWeight: 500,
    fontFamily: 'Poppins-Medium',
    color: '#333',
  },
  rewardAllDivider: {
    height: 1,
    backgroundColor: '#E5E7EB',
    marginVertical: 4,
  },
  rewardAllVoucherName: {
    fontSize: 14,
    fontWeight: 600,
    fontFamily: 'Poppins-SemiBold',
    color: '#222',
    marginTop: 8,
    marginBottom: 6,
  },
  rewardAllValidityNote: {
    fontSize: 14,
    fontFamily: 'Poppins',
    color: '#666',
  },
  rewardAllStatusTitle: {
    fontSize: 14,
    fontWeight: 500,
    fontFamily: 'Poppins-Medium',
    color: '#333',
    marginBottom: 6,
  },
  rewardAllStatusValue: {
    fontSize: 14,
    fontWeight: 600,
    fontFamily: 'Poppins-SemiBold',
    color: '#333',
    marginBottom: 8,
  },
  rewardAllUsageNote: {
    fontSize: 14,
    fontFamily: 'Poppins',
    color: '#666',
  },
  userStats: {
    backgroundColor: '#F8F9FA',
    padding: 15,
    borderRadius: 10,
    marginBottom: 15,
  },
  statsTitle: {
    fontSize: 14,
    lineHeight: 20,
    fontWeight: 500,
    color: '#333',
    marginBottom: 10,
    textAlign: 'center',
    fontFamily: 'Poppins-Medium',
  },
  statsRow: {
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
  statItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  statText: {
    fontSize: 14,
    lineHeight: 20,
    color: '#333',
    marginLeft: 5,
    fontWeight: 600,
    fontFamily: 'Poppins-SemiBold',
  },

  continueButton: {
    margin: 16,
    marginTop: 0,
    paddingVertical: 12,
    borderRadius: 12,
    alignItems: 'center',
  },
  continueButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    lineHeight: 20,
    fontWeight: 500,
    fontFamily: 'Poppins-Medium',
  },

  // Validation Modal Styles
  qrInfoSection: {
    // marginBottom: 20,
    // paddingBottom: 15,
    // borderBottomWidth: 1,
    // borderBottomColor: '#EEEEEE',
  },
  sectionTitle: {
    fontSize: 14,
    lineHeight: 20,
    fontWeight: 'bold',
    color: '#94A3B8',
    marginBottom: 8,
    fontFamily: 'Poppins-Medium',
    fontWeight: 500,
  },
  qrName: {
    fontSize: 14,
    lineHeight: 20,
    fontWeight: '600',
    color: '#0F172A',
    // marginBottom: 5,
    fontFamily: 'Poppins-SemiBold',
    fontWeight: 600,
  },
  qrDescription: {
    fontSize: 12,
    color: '#94A3B8',
    lineHeight: 16,
    marginTop: 4,
    fontFamily: 'Poppins',
  },
  rewardsSection: {
    flexDirection: 'column',
    gap: 4,
    // marginBottom: 20,
  },
  rewardItem: {
    flexDirection: 'row',
    alignItems: 'center',
    // marginBottom: 12,
  },
  rewardText: {
    fontSize: 14,
    lineHeight: 20,
    color: '#0F172A',
    marginLeft: 6,
    fontFamily: 'Poppins-SemiBold',
    fontWeight: 600,
  },

  couponSection: {
    // marginBottom: 20,
    // paddingBottom: 15,
  },
  couponImageContainer: {
    width: '100%',
    height: 120,
    borderRadius: 8,
    overflow: 'hidden',
    marginBottom: 16,
    backgroundColor: '#F1F5F9',
  },
  couponImage: {
    width: '100%',
    height: '100%',
  },
  couponImagePlaceholder: {
    width: '100%',
    height: 120,
    borderRadius: 8,
    backgroundColor: '#F1F5F9',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 12,
  },
  couponName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 5,
  },
  couponDescription: {
    color: '#00C16A',
    fontFamily: 'Poppins-SemiBold',
    fontWeight: 600,
    fontSize: 14,
    lineHeight: 20,
  },
  couponDiscount: {
    fontSize: 14,
    fontWeight: 600,
    color: '#4CAF50',
    // marginBottom: 5,
    fontFamily: 'Poppins-SemiBold',
  },
  couponExpiry: {
    color: '#64748B',
    fontFamily: 'Poppins',
    fontWeight: 300,
    fontStyle: 'italic',
    fontSize: 12,
    lineHeight: 16,
  },
  validitySection: {
    marginBottom: 20,
  },
  validityText: {
    fontSize: 14,
    color: '#666',
    marginBottom: 5,
  },
  validationActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    gap: 12,
  },
  validationButton: {
    flex: 1,
    padding: 12,
    borderRadius: 10,
    alignItems: 'center',
    // marginHorizontal: 10,
  },
  cancelButton: {
    backgroundColor: '#F8F9FA',
    borderWidth: 1,
    borderColor: '#DDDDDD',
  },
  acceptButton: {
    backgroundColor: '#00C16A',
  },
  cancelButtonText: {
    color: '#374151',
    lineHeight: 20,
    fontWeight: 500,
    fontFamily: 'Poppins-Medium'
  },
  acceptButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    lineHeight: 20,
    fontWeight: 500,
    fontFamily: 'Poppins-Medium'
  },

  validationResultHeader: {
    backgroundColor: '#FFFFFF',
  },
  validationResultTitle: {
    color: '#000000'
  },
  validationResultMiniSectionTitle: {
    fontSize: 12,
    marginBottom: 4,
  },
  validationResultMiniSectionName: {
    fontSize: 12,
  },
  validationResultMiniSectionDescription: {
    fontSize: 10,
    marginTop: 2,
  },

  horizontalLine: {
    width: '100%',
    height: 1,
    backgroundColor: "#F1F5F9",
    marginVertical: 16,
  },
  horizontalDashedLine: {
    // width: '100%',
    height: 0,
    borderTopWidth: 1,
    borderStyle: 'dashed',
    borderColor: "#000000",
    marginVertical: 16,
  },

  bannerContainer: {
    width: '100%',
    padding: 16,
    paddingTop: 0,
  },
  banner: {
    position: 'relative',
    width: '100%',
    borderRadius: 6,
    overflow: 'clip',
  },
  bannerImage: {
    width: '100%',
    aspectRatio: 104/30,
  },
  bannerOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: 2,
    backgroundColor: 'rgba(0, 0, 0, 0.25)',
  },
  bannerContent: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: 3,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  bannerLogo: {
    marginBottom: 8,
    minHeight: 16,
    height: 16,
    width: 50,
  },
  bannerTitle: {
    fontFamily: 'Poppins',
    fontSize: 12,
    color: '#ffffff',
  },
  bannerButtonContainer: {
    marginTop: 6,
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 8
  },
  bannerButton: {
    padding: 4,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 6,
  },
  bannerButtonIcon: {
    marginRight: 4,
    color: '#ffffff',
    fontSize: 12,
  },
  bannerButtonText: {
    fontFamily: 'Poppins',
    fontSize: 9,
    color: '#ffffff',
  },
  bannerLoadingText: {
    paddingTop: 10,
    paddingBottom: 10,
    width: '100%',
    textAlign: 'center',
  },

  expiredContainer: {
    paddingBottom: 0
  },
  expiredText: {
    textAlign: 'center'
  },
  expiredDateText: {
    fontSize: 16
  },

  pointWrapper: {
    gap: 1
  },
  pointConvertion: {
    fontSize: 12,
    lineHeight: 16,
    color: '#0F172A',
    marginLeft: 6,
    fontFamily: 'Poppins',
    fontWeight: 400,
  },

  resultHeaderContainer: {
    width: '100%', 
    flexDirection: 'row', 
    gap: 16, 
    padding: 16,
    alignItems: 'center',
  },
  resultTitleWrapper: {
    flex: 1
  },
  resultTitleLeft: {
    textAlign: 'left', 
    marginBottom: 0,
  },
  resultMessageLeft: {
    textAlign: 'left', 
  },
  bannerWrapper: {
    width: '100%',
  },
  disabledButton: {
    opacity: 0.7,
  },
});